import * as React from 'react'
import FillButton from 'components/presenter/buttons/FillButton'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { EmptyValidator, UrlValidator, EmailValidator, MaxLengthValidator } from 'libs/form/validator'
import { useSnackbar } from 'libs/ui/snackbar'
import FormTextField from 'components/presenter/form/FormTextField'
import Form from 'components/presenter/form/Form'
import cn from 'classnames'
import { useStore, companyStoreId } from 'stores'
import { useForm } from 'libs/form/formHook'
import UploadAvatar from 'components/presenter/form/UploadAvatar'
import * as styles from './editProfile.module.css'

const nameId = 'name'
const urlId = 'url'
const emailId = 'email'
const descriptionId = 'description'
const telephoneId = 'telephone'

const EditCompanyProfileSection = () => {
  const { t } = useTranslation()
  const companyStore = useStore(companyStoreId)
  const { showSuccess } = useSnackbar()

  const { onChangeValidation, getFormFieldState, requestAsyc, isFormReady, isLoading, formErrorCode, initForm } = useForm({
    [nameId]: {
      validators: [EmptyValidator],
    },
    [urlId]: {
      isOptional: true,
      validators: [UrlValidator],
    },
    [emailId]: {
      isOptional: true,
      validators: [EmailValidator],
    },
    [descriptionId]: {
      isOptional: true,
      validators: [MaxLengthValidator(2000)],
    },
    [telephoneId]: {
      isOptional: true,
    },
  }, 'update')
  
  const [compProfile, setCompProfile] = React.useState(null)
  React.useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    const _compProfile = companyStore.getters.getCompanyProfile()
    if (_compProfile) {
      setCompProfile(_compProfile)

      initForm({
        [nameId]: {
          value: _compProfile.name
        },
        [urlId]: {
          value: _compProfile.url
        },
        [emailId]: {
          value: _compProfile.email
        },
        [descriptionId]: {
          value: _compProfile.description
        },
        [telephoneId]: {
          value: _compProfile.telephone
        }
      })
    }
  }, [companyStore.getters.getCompanyProfile()])

  const updateCompanyProfile = async () => {
    const response = await requestAsyc(companyStore.actions.updateCompanyProfile({
      name: getFormFieldState(nameId).value,
      url: getFormFieldState(urlId).value,
      telephone: getFormFieldState(telephoneId).value,
      email: getFormFieldState(emailId).value,
      description: getFormFieldState(descriptionId).value,
    }))
    if (!response.error) {
      showSuccess(t('common.success'))
    }
  }

  const uploadProfileImage = async (file) => {
    const response = await requestAsyc(companyStore.actions.uploadCompanyProfileImage(file))
    if (!response.error) {
      showSuccess(t('common.success'))
    }
  }
  
  const name = !compProfile ? '' : compProfile.name 
  const url = !compProfile || !compProfile.profile_image ? null : `${compProfile.profile_image.url}-tn`

  return (
    !compProfile 
    ? <></>
    : <Form className={styles.form} isLoading={isLoading}>
        <UploadAvatar
          className={styles.profileSection}
          name={name}
          url={url}
          uploadBtnText={t('company.uploadLogo')}
          onCrop={uploadProfileImage}
          cropOption={{
            croppedImageWidth: 800,
            croppedImageHeight: 800,
          }}
        />
        <FormTextField
          id={nameId}
          fieldData={getFormFieldState(nameId)}
          className={cn(styles.input)}
          label={t('company.companyName')}
          type="text"
          onChange={onChangeValidation}
        />
        <FormTextField
          id={emailId}
          fieldData={getFormFieldState(emailId)}
          className={cn(styles.input)}
          label={t('company.contactEmail')}
          type="text"
          onChange={onChangeValidation}
        />
        <FormTextField
          id={telephoneId}
          fieldData={getFormFieldState(telephoneId)}
          className={cn(styles.input)}
          label={t('company.contactPhone')}
          type="text"
          onChange={onChangeValidation}
        />
        <FormTextField
          id={urlId}
          fieldData={getFormFieldState(urlId)}
          className={cn(styles.input)}
          label={t('company.companyUrl')}
          placeholder={t('formFields.hint.urlformat')}
          type="text"
          onChange={onChangeValidation}
        />
        <FormTextField
          id={descriptionId}
          multiline
          fieldData={getFormFieldState(descriptionId)}
          className={cn(styles.input, 'mui-textarea')}
          label={t('company.companyIntro')}
          type="text"
          onChange={onChangeValidation}
        />
        <p className={cn(styles.errorMsg)}>{formErrorCode ? `*${t(`errorCodes.${formErrorCode}`)}` : ''}</p>
        <FillButton 
          className={cn(styles.btnUpdate)}
          disabled={!isFormReady()}
          onClick={updateCompanyProfile}
        >
          {t('common.update')}
        </FillButton>
      </Form>
  )
}

export default EditCompanyProfileSection